import { cn } from '@/lib/utils';

interface ClientOrgBadgeProps {
  /** Display name of the client org (e.g. "Mini Zali", "Threadheads") */
  orgName: string;
  className?: string;
  /**
   * Complete accessible label for the badge. The badge is reused far outside
   * the order queues it was built for, so surfaces that are not about orders
   * pass their own noun (e.g. "Mini Zali count"). Defaults to the original
   * `"{org} order"` so existing call sites are unchanged.
   */
  srLabel?: string;
  /**
   * Visible words beside the monogram when they say more than the org's name
   * ("Labelled for Mini Zali"). Defaults to `orgName`; the monogram is still
   * the org's initial. A label never wraps: it truncates once the caller caps
   * the badge's width.
   */
  label?: string;
  /**
   * `compact` (default) is the inventory-app scale this badge was built at.
   * `station` is the production-floor scale PRODUCTION_DESIGN §8 has specified
   * since it was written — operators read station screens from ~3 metres, so
   * `text-[11px]` is unreadable there. Same monogram discipline, larger type.
   */
  size?: 'compact' | 'station';
}

/**
 * BLU-367: small per-order client badge for cross-org rows in shared-location
 * stations. Drop in next to order titles in any queue/list where a Thread Lab
 * operator might see Mini Zali / Threadheads orders interleaved.
 *
 * Visual discipline (per `apps/frontend/DESIGN.md` §13):
 *  - 16×16 monogram circle (single letter, deterministic from org name).
 *  - `bg-muted text-foreground ring-1 ring-border` — NO color fill, so the
 *    badge is colorblind-safe and warehouse-lighting-tolerant. Differentiation
 *    is by initial + name, not hue.
 *  - Suppress at the call site when the queue has only one source org —
 *    a single-tenant queue doesn't need badges.
 */
export function ClientOrgBadge({ orgName, className, srLabel, label, size = 'compact' }: ClientOrgBadgeProps) {
  const initial = (orgName.trim()[0] ?? '?').toUpperCase();
  const station = size === 'station';
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 align-middle', className)}
      aria-label={srLabel ?? `${orgName} order`}
    >
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-foreground ring-1 ring-border',
          station ? 'h-6 w-6 text-sm' : 'h-4 w-4 text-[10px]',
        )}
        aria-hidden="true"
      >
        {initial}
      </span>
      <span
        className={cn(
          'font-medium text-muted-foreground',
          station ? 'text-base' : 'text-[11px]',
          label !== undefined && 'min-w-0 truncate',
        )}
      >
        {label ?? orgName}
      </span>
    </span>
  );
}
